import ImageResizer from '@bam.tech/react-native-image-resizer';
import RNFS from 'react-native-fs';
import { Image } from 'react-native';
import { sanitizeJpegMetadata } from './imageMetadata';

/** Longest edge of the image sent in chat (px). */
export const IMAGE_MAX_EDGE_PX = 2560;
/** Longest edge of the inline preview thumbnail (px). */
export const THUMBNAIL_MAX_EDGE_PX = 320;

/**
 * Во что переупаковывается снимок перед отправкой (стори 13.6).
 *
 * <p>Раньше эти три числа были константами в теле функции, и качество
 * отправляемого фото не выбирал никто: ни пользователь, ни мы осознанно.
 */
export type ImageEncodeProfile = {
  maxEdgePx: number;
  /** 0..100 для выбранного формата. */
  quality: number;
  format: 'WEBP' | 'JPEG';
};

/** Профиль по умолчанию — ровно прежнее поведение приложения. */
export const DEFAULT_IMAGE_ENCODE_PROFILE: ImageEncodeProfile = {
  maxEdgePx: IMAGE_MAX_EDGE_PX,
  quality: 88,
  format: 'WEBP',
};

/** Качество JPEG-фолбэка соответствует WEBP по весу примерно при +2. */
function jpegFallbackQuality(quality: number): number {
  return Math.min(100, quality + 2);
}

/** HEIC/HEIF откроют не все получатели — такие снимки перекодируем всегда. */
export function isHeicLike(mimeType: string | null | undefined, uri: string): boolean {
  const m = (mimeType ?? '').toLowerCase();
  if (m === 'image/heic' || m === 'image/heif' || m === 'image/heic-sequence') return true;
  return /\.hei[cf]$/i.test(uri.split('?')[0]);
}

type ProcessedImageForSend = {
  mimeType: string;
  imageBase64: string;
  thumbnailBase64: string;
};

function stripFileScheme(u: string): string {
  return u.startsWith('file://') ? u.slice('file://'.length) : u;
}

async function readBase64FromPath(uri: string): Promise<string> {
  const path = stripFileScheme(uri);
  return RNFS.readFile(path, 'base64');
}

async function unlinkQuiet(uri: string): Promise<void> {
  try {
    const p = stripFileScheme(uri);
    if (await RNFS.exists(p)) await RNFS.unlink(p);
  } catch {
    /* noop */
  }
}

/**
 * iOS image-picker hands us photo URIs with various schemes:
 *   - ph://<assetId>           (PHAsset reference)
 *   - assets-library://...     (legacy ALAsset)
 *   - file:///private/var/...  (already-copied tmp file)
 * `react-native-image-resizer` only handles file:// / http(s)://. For ph:// /
 * assets-library:// it goes through RCTImageLoader which may fail and the
 * completion block throws an NSException — see the @bam.tech patch. Even with
 * the patch, we'd rather pre-materialise the file ourselves and feed
 * a plain file:// URI to the resizer.
 */
async function materializeIosAsset(uri: string): Promise<string> {
  if (!uri.startsWith('ph://') && !uri.startsWith('assets-library://')) {
    return uri;
  }
  // Copy the source image into a writable cache path. RNFS supports
  // ph://-style URIs through PHImageManager on iOS via copyAssetsFileIOS.
  const cachesDir = (RNFS as unknown as { CachesDirectoryPath?: string }).CachesDirectoryPath;
  if (typeof cachesDir !== 'string' || cachesDir.length === 0) {
    return uri;
  }
  const outName = `vr-pick-${Date.now()}.jpg`;
  const out = `${cachesDir}/${outName}`;
  try {
    const copyFn = (
      RNFS as unknown as {
        copyAssetsFileIOS?: (
          src: string,
          dst: string,
          w: number,
          h: number,
          scale?: number,
          compression?: number,
          resizeMode?: string,
        ) => Promise<string>;
      }
    ).copyAssetsFileIOS;
    if (typeof copyFn === 'function') {
      const copied = await copyFn(uri, out, 0, 0, 1, 1, 'contain');
      const stripped = stripFileScheme(copied);
      if (await RNFS.exists(stripped)) {
        return `file://${stripped}`;
      }
    }
  } catch {
    /* fall through, return original uri */
  }
  return uri;
}

/**
 * Размеры снимка: подсказка от пикера, иначе спрашиваем сам файл.
 *
 * <p>Пикер отдаёт ширину и высоту не всегда — на iOS у ph://-ссылок их часто нет вовсе, а
 * ресайзеру без них не от чего считать масштаб. Спрашиваем систему, а если и она не ответит
 * (та же ph://-ссылка умеет ломать `Image.getSize` уже после нашей материализации) —
 * подставляем щедрый потолок: лучше лишний раз уменьшить, чем не обработать снимок вовсе.
 *
 * <p>Блок был переписан в этом файле четыре раза подряд — отличался только запасным
 * значением.
 */
async function resolveSourceSize(
  uri: string,
  widthHint: number,
  heightHint: number,
  fallbackEdgePx: number,
): Promise<{ width: number; height: number }> {
  let w = widthHint;
  let h = heightHint;
  if (w > 0 && h > 0) {
    return { width: w, height: h };
  }
  try {
    await new Promise<void>((resolve, reject) => {
      Image.getSize(
        uri,
        (iw, ih) => {
          w = iw;
          h = ih;
          resolve();
        },
        reject,
      );
    });
  } catch {
    w = w > 0 ? w : fallbackEdgePx;
    h = h > 0 ? h : fallbackEdgePx;
  }
  return { width: w, height: h };
}


export function buildTargetSize(w: number, h: number, maxEdge: number): { width: number; height: number } {
  const mw = w > 0 ? w : 1;
  const mh = h > 0 ? h : 1;
  const maxDim = Math.max(mw, mh);
  if (maxDim <= maxEdge) {
    return { width: mw, height: mh };
  }
  const scale = maxEdge / maxDim;
  return { width: Math.round(mw * scale), height: Math.round(mh * scale) };
}

function resolveMime(format: 'JPEG' | 'PNG' | 'WEBP'): string {
  if (format === 'WEBP') return 'image/webp';
  if (format === 'PNG') return 'image/png';
  return 'image/jpeg';
}

/**
 * Resize to max edge IMAGE_MAX_EDGE_PX; thumbnail THUMBNAIL_MAX_EDGE_PX.
 */
export async function processImageForSend(
  rawUri: string,
  widthHint: number,
  heightHint: number,
): Promise<ProcessedImageForSend> {
  // On iOS picker URIs can be ph:// or assets-library:// which the resizer
  // routes through RCTImageLoader. Pre-materialise them to plain file:// to
  // avoid the dispatch-queue NSException path that previously crashed the app.
  const uri = await materializeIosAsset(rawUri);

  const { width: w, height: h } = await resolveSourceSize(uri, widthHint, heightHint, IMAGE_MAX_EDGE_PX);

  let fullUri: string;
  let fullW: number;
  let fullH: number;
  let format: 'JPEG' | 'WEBP';

  const sizeFull = buildTargetSize(w, h, IMAGE_MAX_EDGE_PX);
  try {
    const full = await ImageResizer.createResizedImage(
      uri,
      sizeFull.width,
      sizeFull.height,
      'WEBP',
      88,
      0,
      undefined,
      false,
      { mode: 'contain', onlyScaleDown: true },
    );
    fullUri = full.uri;
    fullW = full.width;
    fullH = full.height;
    format = 'WEBP';
  } catch {
    format = 'JPEG';
    const full = await ImageResizer.createResizedImage(
      uri,
      sizeFull.width,
      sizeFull.height,
      'JPEG',
      90,
      0,
      undefined,
      false,
      { mode: 'contain', onlyScaleDown: true },
    );
    fullUri = full.uri;
    fullW = full.width;
    fullH = full.height;
  }

  const mimeType = resolveMime(format);

  const thumbDims = buildTargetSize(fullW, fullH, THUMBNAIL_MAX_EDGE_PX);
  const thumb = await ImageResizer.createResizedImage(
    fullUri,
    thumbDims.width,
    thumbDims.height,
    format === 'WEBP' ? 'WEBP' : 'JPEG',
    format === 'WEBP' ? 82 : 85,
    0,
    undefined,
    false,
    { mode: 'contain', onlyScaleDown: true },
  );

  const imageBase64 = await readBase64FromPath(fullUri);
  const thumbnailBase64 = await readBase64FromPath(thumb.uri);

  await unlinkQuiet(fullUri);
  await unlinkQuiet(thumb.uri);

  if (!imageBase64 || !thumbnailBase64) {
    throw new Error('Image processing did not produce base64');
  }

  return {
    mimeType,
    imageBase64,
    thumbnailBase64,
  };
}

/**
 * Result of {@link processImageForUpload}. The full-resolution file is kept on
 * disk so the caller can stream it as the body of a multipart upload — base64
 * inflation is avoided entirely.
 *
 * <p>The caller MUST invoke {@link releaseProcessedImageUpload} once the upload
 * is finished (or aborted) so the temporary file doesn't leak.
 */
export type ProcessedImageForUpload = {
  mimeType: string;
  fullUri: string;
  width: number;
  height: number;
  thumbnailBase64: string;
  /** Размер файла, который реально уйдёт на сервер (байты), если известен. */
  sizeBytes?: number;
  /**
   * Можно ли удалять `fullUri` после отправки. Для режима «Оригинал» это путь
   * к самому снимку, а не к нашей временной копии, — удалять его нельзя
   * (стори 13.6).
   */
  deleteOnRelease?: boolean;
};

function base64ToBytes(b64: string): Uint8Array {
  const bufferCtor = (globalThis as { Buffer?: typeof Buffer }).Buffer;
  if (typeof bufferCtor !== 'undefined') {
    return new Uint8Array(bufferCtor.from(b64, 'base64'));
  }
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  const bufferCtor = (globalThis as { Buffer?: typeof Buffer }).Buffer;
  if (typeof bufferCtor !== 'undefined') {
    return bufferCtor.from(bytes).toString('base64');
  }
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function isJpegMime(mimeType: string | null | undefined, uri: string): boolean {
  const m = (mimeType ?? '').toLowerCase();
  if (m === 'image/jpeg' || m === 'image/jpg') return true;
  return /\.jpe?g$/i.test(uri.split('?')[0]);
}

/**
 * Убирает из JPEG геометки и данные об устройстве (стори 13.11), **не трогая
 * пиксели**: перекодирование здесь означало бы, что «Оригинал» перестал быть
 * оригиналом.
 *
 * <p>Результат пишется в новый временный файл: исходный снимок — файл
 * пользователя, и переписывать его мы не имеем права.
 *
 * @returns URI очищенной копии либо `null`, если чистить было нечего или
 *          формат не поддерживается.
 */
async function sanitizeImageMetadataToCache(
  uri: string,
  mimeType: string | null | undefined,
): Promise<string | null> {
  if (!isJpegMime(mimeType, uri)) return null;
  const cachesDir = (RNFS as unknown as { CachesDirectoryPath?: string }).CachesDirectoryPath;
  if (typeof cachesDir !== 'string' || cachesDir.length === 0) return null;
  try {
    const original = await RNFS.readFile(stripFileScheme(uri), 'base64');
    const result = sanitizeJpegMetadata(base64ToBytes(original));
    if (!result.changed) return null;
    const out = `${cachesDir}/vr-clean-${Date.now()}.jpg`;
    await RNFS.writeFile(out, bytesToBase64(result.bytes), 'base64');
    return `file://${out}`;
  } catch {
    // Очистка — не повод не отправить фото; на устройстве это видно по тому,
    // что метаданные остались, а не по упавшей отправке.
    return null;
  }
}

async function fileSizeQuiet(uri: string): Promise<number | undefined> {
  try {
    const stat = await RNFS.stat(stripFileScheme(uri));
    const size = typeof stat.size === 'number' ? stat.size : Number(stat.size);
    return Number.isFinite(size) && size > 0 ? size : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Variant of {@link processImageForSend} optimized for the v2 attachment flow:
 * the full image stays on disk so the network layer can stream it as
 * multipart/form-data instead of base64-inflating it into JSON.
 */
export async function processImageForUpload(
  rawUri: string,
  widthHint: number,
  heightHint: number,
  profile: ImageEncodeProfile = DEFAULT_IMAGE_ENCODE_PROFILE,
  options?: { keepMetadata?: boolean },
): Promise<ProcessedImageForUpload> {
  const uri = await materializeIosAsset(rawUri);
  const { width: w, height: h } = await resolveSourceSize(uri, widthHint, heightHint, IMAGE_MAX_EDGE_PX);

  let fullUri: string;
  let fullW: number;
  let fullH: number;
  let format: 'JPEG' | 'WEBP';

  const sizeFull = buildTargetSize(w, h, profile.maxEdgePx);
  try {
    const full = await ImageResizer.createResizedImage(
      uri,
      sizeFull.width,
      sizeFull.height,
      profile.format,
      profile.quality,
      0,
      undefined,
      false,
      { mode: 'contain', onlyScaleDown: true },
    );
    fullUri = full.uri;
    fullW = full.width;
    fullH = full.height;
    format = profile.format;
  } catch {
    // Кодировщик WEBP есть не на всех сборках Android — падаем в JPEG, а не
    // в ошибку отправки.
    format = 'JPEG';
    const full = await ImageResizer.createResizedImage(
      uri,
      sizeFull.width,
      sizeFull.height,
      'JPEG',
      jpegFallbackQuality(profile.quality),
      0,
      undefined,
      false,
      { mode: 'contain', onlyScaleDown: true },
    );
    fullUri = full.uri;
    fullW = full.width;
    fullH = full.height;
  }

  const mimeType = resolveMime(format);
  const thumbnailBase64 = await buildThumbnailBase64(fullUri, fullW, fullH, format);

  // JPEG с выхода кодировщика может унаследовать EXIF исходника (поведение
  // зависит от платформы), поэтому чистим и здесь, а не только в «Оригинале».
  if (!options?.keepMetadata && format === 'JPEG') {
    const cleaned = await sanitizeImageMetadataToCache(fullUri, mimeType);
    if (cleaned) {
      await unlinkQuiet(fullUri);
      fullUri = cleaned;
    }
  }

  const sizeBytes = await fileSizeQuiet(fullUri);
  return {
    mimeType,
    fullUri: /^(file|blob|data|https?):/.test(fullUri) ? fullUri : `file://${fullUri}`,
    width: fullW,
    height: fullH,
    thumbnailBase64,
    ...(typeof sizeBytes === 'number' ? { sizeBytes } : {}),
    deleteOnRelease: true,
  };
}

async function buildThumbnailBase64(
  fullUri: string,
  fullW: number,
  fullH: number,
  format: 'JPEG' | 'WEBP',
): Promise<string> {
  const thumbDims = buildTargetSize(fullW, fullH, THUMBNAIL_MAX_EDGE_PX);
  const thumb = await ImageResizer.createResizedImage(
    fullUri,
    thumbDims.width,
    thumbDims.height,
    format,
    format === 'WEBP' ? 82 : 85,
    0,
    undefined,
    false,
    { mode: 'contain', onlyScaleDown: true },
  );
  const thumbnailBase64 = await readBase64FromPath(thumb.uri);
  await unlinkQuiet(thumb.uri);
  if (!thumbnailBase64) {
    throw new Error('Image processing did not produce thumbnail base64');
  }
  return thumbnailBase64;
}

/**
 * Режим «Оригинал» (стори 13.6): байты снимка не трогаем вообще — единственный
 * честный способ ничего не потерять. Считаем только миниатюру для бабла.
 *
 * <p>Исключение — HEIC/HEIF: его откроют не все получатели, поэтому такой
 * снимок всё-таки перекодируется в JPEG максимального качества.
 */
export async function prepareOriginalImageForUpload(
  rawUri: string,
  widthHint: number,
  heightHint: number,
  sourceMimeType?: string | null,
  options?: { keepMetadata?: boolean },
): Promise<ProcessedImageForUpload> {
  const uri = await materializeIosAsset(rawUri);

  if (isHeicLike(sourceMimeType, uri)) {
    return processImageForUpload(rawUri, widthHint, heightHint, {
      // Размер не режем — «Оригинал» остаётся оригиналом по геометрии.
      maxEdgePx: Number.MAX_SAFE_INTEGER,
      quality: 100,
      format: 'JPEG',
    });
  }

  const { width: w, height: h } = await resolveSourceSize(uri, widthHint, heightHint, IMAGE_MAX_EDGE_PX);

  // Миниатюра — JPEG: она нужна везде, а WEBP-кодировщик есть не всегда.
  const thumbnailBase64 = await buildThumbnailBase64(uri, w, h, 'JPEG');
  const mimeType =
    typeof sourceMimeType === 'string' && sourceMimeType.startsWith('image/')
      ? sourceMimeType
      : /\.png$/i.test(uri.split('?')[0])
        ? 'image/png'
        : 'image/jpeg';

  // Геометки и данные устройства убираем до отправки (стори 13.11). Очищенная
  // копия — уже наш временный файл, её и удаляем после заливки; исходный
  // снимок пользователя не трогаем ни в каком случае.
  const cleaned = options?.keepMetadata
    ? null
    : await sanitizeImageMetadataToCache(uri, mimeType);
  const sendUri = cleaned ?? uri;
  const sizeBytes = await fileSizeQuiet(sendUri);

  return {
    mimeType,
    fullUri: /^(file|blob|data|https?):/.test(sendUri) ? sendUri : `file://${sendUri}`,
    width: w,
    height: h,
    thumbnailBase64,
    ...(typeof sizeBytes === 'number' ? { sizeBytes } : {}),
    deleteOnRelease: cleaned !== null,
  };
}

/** Максимальная сторона аватара, отправляемого на сервер. */
const AVATAR_MAX_EDGE_PX = 1280;

type ProcessedAvatarForUpload = {
  uri: string;
  mimeType: string;
};

/**
 * Готовит выбранное фото к загрузке в аватар.
 *
 * Раньше файл из пикера уходил как есть, а сервер режет аватар на 8 МБ
 * (`SquareAvatarImageProcessor.MAX_UPLOAD_BYTES`). На iOS пикер переупаковывает
 * снимок и он почти всегда влезал, на Android — нет: снимок современной камеры
 * легко больше 8 МБ, и пользователь получал «Не удалось загрузить фото»
 * (стори 5.3).
 *
 * Формат — JPEG, не WEBP: серверный `ImageIO.read` WEBP не читает.
 */
export async function processAvatarForUpload(
  rawUri: string,
  widthHint = 0,
  heightHint = 0,
): Promise<ProcessedAvatarForUpload> {
  const uri = await materializeIosAsset(rawUri);
  const { width: w, height: h } = await resolveSourceSize(uri, widthHint, heightHint, AVATAR_MAX_EDGE_PX);
  const size = buildTargetSize(w, h, AVATAR_MAX_EDGE_PX);
  const out = await ImageResizer.createResizedImage(
    uri,
    size.width,
    size.height,
    'JPEG',
    88,
    0,
    undefined,
    false,
    { mode: 'contain', onlyScaleDown: true },
  );
  return {
    uri: /^(file|blob|data|https?):/.test(out.uri) ? out.uri : `file://${out.uri}`,
    mimeType: 'image/jpeg',
  };
}

/**
 * Best-effort cleanup of the temp file created by {@link processImageForUpload}.
 *
 * <p>`deleteOnRelease: false` означает, что путь ведёт не в нашу временную
 * копию, а в файл пользователя (режим «Оригинал») — такой не трогаем.
 */
export async function releaseProcessedImageUpload(processed: {
  fullUri: string;
  deleteOnRelease?: boolean;
}): Promise<void> {
  if (processed.deleteOnRelease === false) return;
  await unlinkQuiet(processed.fullUri);
}
